/**
 * Apresentação dos valores canônicos vindos da API.
 *
 * Papel: aqui ficam as funções que traduzem o que está guardado no banco para o
 * que a pessoa lê na tela — telefone com parênteses, CPF com pontos, data em
 * ordem brasileira, dinheiro com cifrão, pessoa da
 * família com o apelido entre parênteses, lista de nomes em caixa alta.
 *
 * Como usar: nenhuma tela monta esse texto por conta própria. Um valor que chega
 * da API é sempre canônico — telefone é `11987654321`, data é `2026-08-31`,
 * dinheiro é número — e é aqui que ele vira legível; escrever a formatação na
 * página faz surgir uma segunda grafia do mesmo dado, e duas telas passam a
 * mostrar o mesmo telefone de formas diferentes.
 *
 * As que recebem um valor que pode faltar aceitam `null` e devolvem um
 * travessão, porque ausência é comum — um documento sem validade, um contato
 * sem telefone —, e a alternativa seria cada tabela repetir o mesmo teste antes
 * de chamar. `formatarPessoa` é a exceção: a pessoa de um documento ou de uma
 * apólice sempre existe.
 */

/** Texto exibido quando o valor não existe. */
const AUSENTE = '—';

/**
 * Escreve um telefone de dez ou onze dígitos na forma brasileira.
 *
 * Decide a posição do hífen pela contagem: onze dígitos é celular e quebra
 * depois do quinto, dez é fixo e quebra depois do quarto. Contagem inesperada
 * é devolvida como veio, em vez de recortada — um número torto na tela avisa
 * que há dado a corrigir, enquanto um recorte silencioso o esconderia.
 *
 * @param digitos - Só dígitos, como `telefoneOpcionalSchema` devolve.
 * @returns `(11) 98765-4321`, `(11) 3456-7890`, ou o travessão quando ausente.
 */
export function formatarTelefone(digitos: string | null | undefined): string {
  if (digitos == null || digitos === '') return AUSENTE;
  if (digitos.length === 11) {
    return `(${digitos.slice(0, 2)}) ${digitos.slice(2, 7)}-${digitos.slice(7)}`;
  }
  if (digitos.length === 10) {
    return `(${digitos.slice(0, 2)}) ${digitos.slice(2, 6)}-${digitos.slice(6)}`;
  }
  return digitos;
}

/**
 * Escreve um CPF de onze dígitos com a pontuação habitual.
 *
 * @param digitos - Onze dígitos, como `cpfSchema` devolve.
 * @returns `123.456.789-01`, ou o travessão quando ausente.
 */
export function formatarCpf(digitos: string | null | undefined): string {
  if (digitos == null || digitos === '') return AUSENTE;
  if (digitos.length !== 11) return digitos;
  return `${digitos.slice(0, 3)}.${digitos.slice(3, 6)}.${digitos.slice(6, 9)}-${digitos.slice(9)}`;
}

/**
 * Escreve um CEP de oito dígitos com o hífen.
 *
 * @param digitos - Oito dígitos, como `cepOpcionalSchema` devolve.
 * @returns `01001-000`, ou o travessão quando ausente.
 */
export function formatarCep(digitos: string | null | undefined): string {
  if (digitos == null || digitos === '') return AUSENTE;
  if (digitos.length !== 8) return digitos;
  return `${digitos.slice(0, 5)}-${digitos.slice(5)}`;
}

/**
 * Escreve uma placa, pondo o hífen apenas no padrão antigo.
 *
 * A placa Mercosul é escrita sem separador — `ABC1D23` —, enquanto a antiga
 * leva hífen antes dos quatro dígitos. Aplicar o hífen nas duas faria a placa
 * Mercosul aparecer na tela diferente do que está no documento do carro.
 *
 * @param placa - Placa canônica, em maiúsculas e sem separador.
 * @returns `ABC1D23`, `ABC-1234`, ou o travessão quando ausente.
 */
export function formatarPlaca(placa: string | null | undefined): string {
  if (placa == null || placa === '') return AUSENTE;
  if (/^[A-Z]{3}\d{4}$/.test(placa)) return `${placa.slice(0, 3)}-${placa.slice(3)}`;
  return placa;
}

/**
 * Escreve um valor em reais.
 *
 * O espaço depois de `R$` é trocado pelo espaço comum, porque
 * `toLocaleString` produz um espaço não separável ali. O caractere é
 * tipograficamente correto, mas invisível em tudo o mais: uma busca por
 * `"R$ 1.234"` digitada no campo de busca não encontraria a linha, e uma
 * comparação de texto falharia sem que a diferença aparecesse na tela.
 *
 * @param valor - Número como a API devolve, já com duas casas.
 * @returns `R$ 1.234,56`, ou o travessão quando ausente.
 */
export function formatarDinheiro(valor: number | null | undefined): string {
  if (valor == null || !Number.isFinite(valor)) return AUSENTE;
  return valor
    .toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
    .replace(/\u00a0/g, ' ');
}

/**
 * Escreve um número inteiro com separador de milhar e sem casas decimais.
 *
 * Existe ao lado de `formatarNumero` porque aquele força duas casas — o que
 * serve a um valor em reais e não a uma contagem. Quilometragem e lotação são
 * contagens, e `12.345,00 km` diz uma precisão que a medida não tem.
 *
 * **Ano não passa por aqui.** O separador de milhar transformaria `2019` em
 * `2.019`; ano é um rótulo escrito por extenso, e vai para a tela como está.
 *
 * @param valor - O número a escrever; ausente ou não finito vira travessão.
 * @returns O número com ponto a cada milhar, ou o travessão.
 */
export function formatarInteiro(valor: number | null | undefined): string {
  if (valor == null || !Number.isFinite(valor)) return AUSENTE;
  return valor.toLocaleString('pt-BR', { maximumFractionDigits: 0 });
}

/**
 * Escreve um número sem cifrão, com duas casas e separador de milhar.
 *
 * Serve às colunas de tabela que já anunciam a moeda no cabeçalho, onde repetir
 * `R$` em cada linha só afasta os dígitos uns dos outros.
 *
 * @param valor - Número como a API devolve.
 * @returns `1.234,56`, ou o travessão quando ausente.
 */
export function formatarNumero(valor: number | null | undefined): string {
  if (valor == null || !Number.isFinite(valor)) return AUSENTE;
  return valor.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/**
 * Escreve uma data ISO na ordem brasileira.
 *
 * Fatia o texto em vez de construir um `Date`, porque `new Date('2026-08-31')`
 * é interpretado como meia-noite em UTC e, no fuso de Brasília, volta como o
 * dia 30 — o erro de um dia que aparece em toda tabela de vencimento.
 *
 * @param iso - Data `AAAA-MM-DD`, como a API devolve.
 * @returns `31/08/2026`, ou o travessão quando ausente.
 */
export function formatarData(iso: string | null | undefined): string {
  if (iso == null || iso === '') return AUSENTE;
  const [ano, mes, dia] = iso.slice(0, 10).split('-');
  if (ano === undefined || mes === undefined || dia === undefined) return iso;
  return `${dia}/${mes}/${ano}`;
}

/**
 * Relógio usado para escrever instantes, fixado no fuso do escritório.
 *
 * O fuso é explícito, e não o do navegador, porque os registros descrevem o que
 * aconteceu nas casas e no escritório: quem abrir a tela em viagem, noutro fuso,
 * deverá ler o mesmo horário que quem está em São Paulo.
 */
const RELOGIO = new Intl.DateTimeFormat('pt-BR', {
  timeZone: 'America/Sao_Paulo',
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

/**
 * Escreve um instante com data e hora, no horário de Brasília.
 *
 * Diferente de `formatarData`, aqui o `Date` é necessário e correto: o valor
 * carrega fuso (`2026-09-15T17:42:08Z`), e é justamente a conversão para o fuso
 * do escritório que se quer. Usar `formatarData` num instante mostraria o dia
 * em UTC, errado para tudo o que acontece depois das nove da noite.
 *
 * @param iso - Instante ISO 8601 com fuso, como `toISOString` produz.
 * @returns `15/09/2026 14:42`, ou o travessão quando ausente ou ilegível.
 */
export function formatarInstante(iso: string | null | undefined): string {
  if (iso == null || iso === '') return AUSENTE;
  const instante = new Date(iso);
  if (Number.isNaN(instante.getTime())) return AUSENTE;
  return RELOGIO.format(instante).replace(', ', ' ');
}

/** Nomes curtos dos meses, na ordem em que `competencia` os indexa. */
const MESES_CURTOS = [
  'Jan',
  'Fev',
  'Mar',
  'Abr',
  'Mai',
  'Jun',
  'Jul',
  'Ago',
  'Set',
  'Out',
  'Nov',
  'Dez',
] as const;

/**
 * Escreve uma competência mensal por extenso abreviado.
 *
 * @param competencia - `AAAA-MM`, ou uma data ISO da qual só o mês interessa.
 * @returns `Ago/2026`, ou o travessão quando ausente.
 */
export function formatarCompetencia(competencia: string | null | undefined): string {
  if (competencia == null || competencia === '') return AUSENTE;
  const [ano, mes] = competencia.slice(0, 7).split('-');
  if (ano === undefined || mes === undefined) return competencia;
  const nome = MESES_CURTOS[Number(mes) - 1];
  return nome === undefined ? competencia : `${nome}/${ano}`;
}

/** A parte de uma pessoa da família que o nome por extenso usa. */
interface NomeDePessoa {
  nomeCompleto: string;
  /** O nome curto; vazio, só de espaços ou ausente quando a pessoa não tem um. */
  apelido?: string | null;
}

/**
 * Escreve uma pessoa da família pelo nome completo, com o apelido entre
 * parênteses.
 *
 * É a forma com que as tabelas de Documentos e de Saúde e o aviso de
 * vencimento nomeiam a pessoa, e por isso sai daqui, e não de cada tela: o
 * mesmo passaporte não deverá aparecer como `Fulano de Tal (Fulano)` na tabela
 * e como `Fulano de Tal` no aviso. O apelido vazio some junto com os
 * parênteses, em vez de deixar um `()` no fim do nome.
 *
 * @param pessoa - A pessoa como a API a aninha no registro, com `nomeCompleto`
 *   e `apelido`.
 * @returns `Nome Completo (Apelido)`, ou só o nome completo quando não há
 *   apelido.
 */
export function formatarPessoa(pessoa: NomeDePessoa): string {
  const apelido = pessoa.apelido?.trim() ?? '';
  return apelido === '' ? pessoa.nomeCompleto : `${pessoa.nomeCompleto} (${apelido})`;
}

/**
 * Junta nomes numa lista em caixa alta, com vírgula entre eles e " E " antes
 * do último.
 *
 * É a forma dos subtítulos de seção que enumeram quem ou o quê a seção cobre —
 * os apelidos das pessoas dos documentos, as operadoras dos planos —, e por
 * isso sai daqui, e não de cada tela: duas telas montando a enumeração à mão
 * acabariam uma com "E" e outra com vírgula antes do último. Cada nome passa a
 * caixa alta antes da comparação, então `Fulano` e `FULANO` contam como um só,
 * e vale a posição da primeira vez em que ele aparece; nome vazio ou só de
 * espaços fica de fora.
 *
 * @param nomes - Os nomes na ordem em que devem aparecer, com repetição
 *   permitida — o chamador passa um por registro, sem precisar deduplicar.
 * @returns `A`, `A E B`, `A, B E C`, ou texto vazio quando não sobra nome
 *   nenhum — e cabe ao chamador omitir o separador que viria antes da lista.
 */
export function formatarListaEmCaixaAlta(nomes: readonly string[]): string {
  const distintos = [
    ...new Set(
      nomes
        .map((nome) => nome.trim().toLocaleUpperCase('pt-BR'))
        .filter((nome) => nome !== ''),
    ),
  ];
  if (distintos.length <= 1) return distintos.join('');
  return `${distintos.slice(0, -1).join(', ')} E ${distintos.at(-1) ?? ''}`;
}
